import React, { useState, useEffect } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { useAuth } from '../context/AuthContext';
import EnhancedPaymentPortal from './EnhancedPaymentPortal';
import AdminReports from './AdminReports';
import SimpleUserManagement from './SimpleUserManagement';
import UserManagementPanel from './UserManagementPanel';
import { 
  Shield,
  Users,
  DollarSign,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Download,
  FileText,
  Search,
  Filter,
  Eye,
  Edit,
  RefreshCw,
  BarChart3,
  Calendar,
  CreditCard,
  Banknote,
  User,
  Mail,
  Phone,
  Hash,
  MapPin,
  CreditCard as CreditCardIcon
} from 'lucide-react';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount || 0);
};

const ComprehensiveAdminDashboard = () => {
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Dashboard data
  const [dashboardData, setDashboardData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [agents, setAgents] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  
  // Payment Links data
  const [paymentLinks, setPaymentLinks] = useState([]);
  const [linksLoading, setLinksLoading] = useState(false);
  
  // Filters and search
  const [filters, setFilters] = useState({
    dateRange: '30',
    status: 'all',
    agent: 'all',
    paymentMethod: 'all',
    minAmount: '',
    maxAmount: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Advanced client search
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [clientSearch, setClientSearch] = useState({
    name: '',
    email: '',
    phone: '',
    policyNumber: ''
  });
  const [clientSearchResults, setClientSearchResults] = useState([]);

  // Modal states
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);

  useEffect(() => {
    loadDashboardData();
    loadAgentsList();
  }, [filters]);

  useEffect(() => {
    loadTransactions();
  }, [filters, searchTerm, sortField, sortOrder]);

  useEffect(() => {
    console.log('TAB CHANGE DEBUG:', { 
      activeTab, 
      isUsersTab: activeTab === 'users',
      typeof_activeTab: typeof activeTab,
      stringified: JSON.stringify(activeTab)
    });
    
    // Send debug info to server so MCP can see it
    fetch('/api/admin/debug-log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: 'TAB_CHANGE_DEBUG',
        data: { 
          activeTab, 
          isUsersTab: activeTab === 'users',
          typeof_activeTab: typeof activeTab,
          stringified: JSON.stringify(activeTab),
          timestamp: new Date().toISOString()
        }
      })
    }).catch(e => console.log('Debug log failed:', e));
    
    if (activeTab === 'users') {
      console.log('activeTab changed to users, calling fetchAllUsers');
      fetchAllUsers();
    } else if (activeTab === 'links') {
      loadPaymentLinks();
    }
  }, [activeTab]);

  useEffect(() => {
    console.log('allUsers state changed:', allUsers.length, 'users');
  }, [allUsers]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/admin/dashboard', {
        params: {
          dateRange: filters.dateRange,
          status: filters.status,
          agent: filters.agent
        }
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTransactions = async () => {
    try {
      const response = await axiosInstance.get('/admin/transactions', {
        params: {
          ...filters,
          search: searchTerm,
          sortField,
          sortOrder,
          limit: 50
        }
      });
      setTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Error loading transactions:', error);
    }
  };

  const loadAgentsList = async () => {
    try {
      const response = await axiosInstance.get('/admin/agents');
      setAgents(response.data.agents || []);
    } catch (error) {
      console.error('Error loading agents:', error);
    }
  };

  const fetchAllUsers = async () => {
    try {
      console.log('Fetching users...');
      setUsersLoading(true);
      const response = await axiosInstance.get('/auth/users', {
        params: { limit: 100 } // Get all users at once
      });
      console.log('Users response:', response.data);
      setAllUsers(response.data.users || []);
      console.log('Users set:', response.data.users?.length || 0, 'users');
    } catch (error) {
      console.error('Error loading users:', error);
      console.error('Error details:', error.response?.data);
      alert('Error loading users: ' + (error.response?.data?.error || error.message));
    } finally {
      setUsersLoading(false);
    }
  };

  const unlockUserAccount = async (email) => {
    if (!confirm(`Are you sure you want to unlock the account for ${email}?`)) {
      return;
    }

    setUnlockLoading(true);
    try {
      const response = await axiosInstance.post('/unlock-account', {
        email: email,
        confirmationKey: 'PAYLOW_UNLOCK_ACCOUNTS_2024'
      });

      alert(`Success: ${response.data.message}`);
      
      // Refresh the users list
      await fetchAllUsers();
    } catch (error) {
      console.error('Error unlocking account:', error);
      alert('Error unlocking account: ' + (error.response?.data?.error || error.message));
    } finally {
      setUnlockLoading(false);
    }
  };

  const loadPaymentLinks = async () => {
    try {
      setLinksLoading(true);
      const response = await axiosInstance.get('/payments/declined-links', {
        params: { limit: 100 }
      });
      setPaymentLinks(response.data.links || []);
    } catch (error) {
      console.error('Error loading payment links:', error);
      alert('Error loading payment links: ' + (error.response?.data?.error || error.message));
    } finally {
      setLinksLoading(false);
    }
  };

  const deactivateLink = async (linkId) => {
    if (!confirm('Are you sure you want to deactivate this payment link?')) {
      return;
    }
    
    try {
      await axiosInstance.put(`/payments/declined-link/${linkId}/deactivate`);
      alert('Payment link deactivated successfully');
      loadPaymentLinks();
    } catch (error) {
      alert(`Error deactivating link: ${error.response?.data?.error || error.message}`);
    }
  };

  const extendLink = async (linkId) => {
    if (!confirm('Extend this payment link by 3 days?')) {
      return;
    }
    
    try {
      await axiosInstance.put(`/payments/declined-link/${linkId}/extend`);
      alert('Payment link extended by 3 days');
      loadPaymentLinks();
    } catch (error) {
      alert(`Error extending link: ${error.response?.data?.error || error.message}`);
    }
  };

  const lockUserAccount = async (email) => {
    if (!confirm(`Are you sure you want to lock the account for ${email}? They will be unable to login.`)) {
      return;
    }
    
    try {
      // Use the existing User model update to set lockUntil field
      const response = await axiosInstance.post('/admin/lock-user', {
        email: email,
        reason: 'Admin locked via dashboard'
      });

      alert(`Successfully locked account for ${email}`);
      
      // Refresh the users list
      await fetchAllUsers();
    } catch (error) {
      console.error('Error locking user account:', error);
      alert(`Error locking account: ${error.response?.data?.error || error.message}`);
    }
  };

  // Advanced client search function
  const searchClients = async () => {
    try {
      const searchParams = {};
      
      // Only include non-empty search fields
      if (clientSearch.name.trim()) searchParams.name = clientSearch.name.trim();
      if (clientSearch.email.trim()) searchParams.email = clientSearch.email.trim();
      if (clientSearch.phone.trim()) searchParams.phone = clientSearch.phone.trim();
      if (clientSearch.policyNumber.trim()) searchParams.policyNumber = clientSearch.policyNumber.trim();
      
      if (Object.keys(searchParams).length === 0) {
        alert('Please enter at least one search criteria');
        return;
      }

      const response = await axiosInstance.post('/admin/search-clients', searchParams);
      setClientSearchResults(response.data.results || []);
    } catch (error) {
      console.error('Error searching clients:', error);
      alert('Error searching clients. Please try again.');
    }
  };

  // Clear client search
  const clearClientSearch = () => {
    setClientSearch({
      name: '',
      email: '',
      phone: '',
      policyNumber: ''
    });
    setClientSearchResults([]);
  };

  const exportData = async (type) => {
    try {
      const response = await axiosInstance.get(`/admin/export/${type}`, {
        params: filters,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}-export-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting data:', error);
    }
  };

  const generateReceipt = async (transactionId, paymentId) => {
    try {
      const response = await axiosInstance.get(`/admin/receipt/${transactionId}/${paymentId}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `receipt-${transactionId}-${paymentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error generating receipt:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-700 bg-green-100';
      case 'declined': 
      case 'failed': return 'text-red-700 bg-red-100';
      case 'scheduled':
      case 'pending': return 'text-yellow-700 bg-yellow-100';
      case 'processing': return 'text-blue-700 bg-blue-100';
      default: return 'text-gray-700 bg-gray-100';
    }
  };

  const getPaymentMethodIcon = (method, isCompToComp = false) => {
    if (isCompToComp) {
      return <div className="flex items-center"><CreditCard size={16} className="text-purple-600 mr-1" /><span className="text-xs text-purple-600">COMP</span></div>;
    }
    if (method === 'cash') {
      return <Banknote size={16} className="text-green-600" />;
    }
    return <CreditCard size={16} className="text-gray-400" />;
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'text-red-700 bg-red-100 border-red-200';
      case 'high': return 'text-orange-700 bg-orange-100 border-orange-200';
      case 'medium': return 'text-yellow-700 bg-yellow-100 border-yellow-200';
      case 'low': return 'text-green-700 bg-green-100 border-green-200';
      default: return 'text-gray-700 bg-gray-100 border-gray-200';
    }
  };

  const stats = dashboardData?.stats || {};

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center">
          <RefreshCw className="animate-spin h-8 w-8 text-blue-600 mb-4" />
          <span className="text-gray-600">Loading admin dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-blue-600 via-blue-700 to-red-600 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <div className="p-2 bg-white bg-opacity-20 rounded-lg mr-4">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <div>
                <div className="flex items-center space-x-4">
                  <h1 className="text-2xl font-bold text-white">
                    Admin Dashboard
                  </h1>
                  <div className="paylow-logo text-xl">
                    <span className="text-white font-bold">Pay</span><span className="text-red-200 font-bold">Low</span>
                  </div>
                </div>
                <p className="text-blue-100 mt-1">
                  System Administrator - {user?.firstName} {user?.lastName}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => loadDashboardData()}
                className="p-2 text-white hover:text-gray-200 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
              >
                <RefreshCw size={20} />
              </button>
              <button
                onClick={() => window.open('/admin/utilities', '_blank')}
                className="bg-white bg-opacity-20 text-white px-4 py-2 rounded-lg hover:bg-opacity-30 transition-colors backdrop-blur-sm"
              >
                🔧 Admin Utilities
              </button>
              <button
                onClick={logout}
                className="bg-red-500 bg-opacity-90 text-white px-4 py-2 rounded-lg hover:bg-opacity-100 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <div className="bg-gradient-to-br from-green-50 to-white rounded-xl shadow-sm p-6 border border-green-100 hover:shadow-md transition-all duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {formatCurrency(stats.totalRevenue)}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl shadow-sm p-6 border border-blue-100 hover:shadow-md transition-all duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Transactions</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {stats.totalTransactions || 0}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <TrendingUp className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-white rounded-xl shadow-sm p-6 border border-purple-100 hover:shadow-md transition-all duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Agents</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {stats.activeAgents || 0}
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <Users className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-red-50 to-white rounded-xl shadow-sm p-6 border border-red-100 hover:shadow-md transition-all duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Declined Amount</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {formatCurrency(stats.totalDeclined)}
                </p>
              </div>
              <div className="p-3 bg-red-100 rounded-full">
                <XCircle className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-white rounded-xl shadow-sm p-6 border border-orange-100 hover:shadow-md transition-all duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending Reviews</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {stats.pendingReviews || 0}
                </p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <AlertTriangle className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-gradient-to-r from-white to-gray-50 rounded-xl shadow-sm p-6 border border-gray-100 mb-6">
          <div className="flex flex-wrap items-center gap-4 mb-4">
            <div className="flex items-center space-x-2">
              <Filter size={20} className="text-gray-500" />
              <span className="font-medium text-gray-700">Filters:</span>
            </div>
            
            <select
              value={filters.dateRange}
              onChange={(e) => setFilters({...filters, dateRange: e.target.value})}
              className="form-select rounded-lg border-gray-300"
            >
              <option value="7">Last 7 Days</option>
              <option value="30">Last 30 Days</option>
              <option value="90">Last 90 Days</option>
              <option value="365">Last Year</option>
              <option value="all">All Time</option>
            </select>

            <select
              value={filters.agent}
              onChange={(e) => setFilters({...filters, agent: e.target.value})}
              className="form-select rounded-lg border-gray-300"
            >
              <option value="all">All Agents</option>
              {agents.map(agent => (
                <option key={agent.agentId} value={agent.agentId}>
                  {agent.firstName} {agent.lastName} ({agent.agentId})
                </option>
              ))}
            </select>

            <select
              value={filters.status}
              onChange={(e) => setFilters({...filters, status: e.target.value})}
              className="form-select rounded-lg border-gray-300"
            >
              <option value="all">All Statuses</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="declined">Declined</option>
              <option value="failed">Failed</option>
            </select>

            <select
              value={filters.paymentMethod}
              onChange={(e) => setFilters({...filters, paymentMethod: e.target.value})}
              className="form-select rounded-lg border-gray-300"
            >
              <option value="all">All Payment Methods</option>
              <option value="card">Credit Card</option>
              <option value="cash">Cash</option>
              <option value="cc_to_comp">CC to COMP</option>
            </select>

            <div className="flex items-center space-x-2">
              <input
                type="number"
                placeholder="Min Amount"
                value={filters.minAmount}
                onChange={(e) => setFilters({...filters, minAmount: e.target.value})}
                className="form-input rounded-lg border-gray-300 w-32"
              />
              <span className="text-gray-500">to</span>
              <input
                type="number"
                placeholder="Max Amount"
                value={filters.maxAmount}
                onChange={(e) => setFilters({...filters, maxAmount: e.target.value})}
                className="form-input rounded-lg border-gray-300 w-32"
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="relative">
              <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search transactions, clients, emails..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-96 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
                className={`flex items-center text-sm px-3 py-2 rounded-lg transition-all ${
                  showAdvancedSearch 
                    ? 'bg-blue-600 text-white' 
                    : 'text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200'
                }`}
              >
                <User size={16} className="mr-2" />
                Client Search
              </button>
              <button
                onClick={() => exportData('transactions')}
                className="flex items-center text-sm text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-lg"
              >
                <Download size={16} className="mr-2" />
                Export CSV
              </button>
              <button
                onClick={() => exportData('reports')}
                className="flex items-center text-sm text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-lg"
              >
                <BarChart3 size={16} className="mr-2" />
                Generate Report
              </button>
            </div>
          </div>
        </div>

        {/* Advanced Client Search */}
        {showAdvancedSearch && (
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                <User size={20} className="text-blue-600 mr-2" />
                Advanced Client Search
              </h3>
              <button
                onClick={() => setShowAdvancedSearch(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle size={20} />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <div className="relative">
                <User size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Client Name"
                  value={clientSearch.name}
                  onChange={(e) => setClientSearch({...clientSearch, name: e.target.value})}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div className="relative">
                <Mail size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  placeholder="Email Address"
                  value={clientSearch.email}
                  onChange={(e) => setClientSearch({...clientSearch, email: e.target.value})}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div className="relative">
                <Phone size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="tel"
                  placeholder="Phone Number"
                  value={clientSearch.phone}
                  onChange={(e) => setClientSearch({...clientSearch, phone: e.target.value})}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div className="relative">
                <Hash size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Policy Number"
                  value={clientSearch.policyNumber}
                  onChange={(e) => setClientSearch({...clientSearch, policyNumber: e.target.value})}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Enter any combination of search criteria to find client records and transaction history
              </p>
              <div className="flex items-center space-x-3">
                <button
                  onClick={clearClientSearch}
                  className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all"
                >
                  Clear
                </button>
                <button
                  onClick={searchClients}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all flex items-center"
                >
                  <Search size={16} className="mr-2" />
                  Search Clients
                </button>
              </div>
            </div>
            
            {/* Client Search Results */}
            {clientSearchResults.length > 0 && (
              <div className="mt-6 border-t pt-6">
                <h4 className="text-md font-semibold text-gray-800 mb-4">
                  Search Results ({clientSearchResults.length} clients found)
                </h4>
                <div className="space-y-4">
                  {clientSearchResults.map((client, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 flex-1">
                          <div>
                            <p className="text-sm font-medium text-gray-600">Name</p>
                            <p className="text-sm text-gray-900">{client.name}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-600">Email</p>
                            <p className="text-sm text-gray-900">{client.email}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-600">Phone</p>
                            <p className="text-sm text-gray-900">{client.phone || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-600">Transactions</p>
                            <p className="text-sm text-gray-900">{client.transactionCount} payments</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                          {client.ghlContactId && (
                            <a
                              href={`https://app.gohighlevel.com/contacts/${client.ghlContactId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-3 py-1 bg-green-100 text-green-700 rounded-lg text-xs hover:bg-green-200 transition-all"
                            >
                              GHL Profile
                            </a>
                          )}
                          <span className="text-xs text-gray-500">
                            Total: {formatCurrency(client.totalPaid)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tab Navigation */}
        <div className="mb-6">
          <nav className="flex space-x-8">
            {[
              { key: 'overview', label: 'Overview' },
              { key: 'process', label: 'Process Payments', icon: CreditCardIcon },
              { key: 'transactions', label: 'All Transactions', count: transactions.length },
              { key: 'agents', label: 'Agent Performance', count: agents.length },
              { key: 'reports', label: 'Reports & Analytics' },
              { key: 'links', label: 'Active Payment Links' }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`pb-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.key
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-blue-600 hover:border-blue-300'
                }`}
              >
                <div className="flex items-center">
                  {tab.icon && <tab.icon size={16} className="mr-2" />}
                  {tab.label}
                  {tab.count !== undefined && (
                    <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                      activeTab === tab.key 
                        ? 'bg-red-100 text-red-600' 
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Recent High-Priority Transactions */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="p-6 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900">Priority Transactions</h3>
                <p className="text-sm text-gray-600 mt-1">High-value or problematic transactions requiring attention</p>
              </div>
              <div className="divide-y divide-gray-100">
                {dashboardData?.priorityTransactions?.map((transaction) => (
                  <div key={transaction._id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">
                              {transaction.client.name}
                            </p>
                            <p className="text-sm text-gray-500">
                              Agent: {transaction.agentId} • {formatCurrency(transaction.amounts.total)}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getPriorityColor(transaction.adminFlags?.priority)}`}>
                              {transaction.adminFlags?.priority || 'medium'}
                            </span>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(transaction.status)}`}>
                              {transaction.status}
                            </span>
                          </div>
                        </div>
                      </div>
                      <button 
                        onClick={() => {
                          setSelectedTransaction(transaction);
                          setShowTransactionModal(true);
                        }}
                        className="ml-4 text-blue-600 hover:text-blue-900"
                      >
                        <Eye size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Agent Performance Summary */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="p-6 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900">Top Performing Agents</h3>
                <p className="text-sm text-gray-600 mt-1">Revenue and transaction volume leaders</p>
              </div>
              <div className="p-6 space-y-4">
                {dashboardData?.topAgents?.map((agent, index) => (
                  <div key={agent.agentId} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                        index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-orange-400' : 'bg-gray-300'
                      }`}>
                        {index + 1}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {agent.firstName} {agent.lastName}
                        </p>
                        <p className="text-sm text-gray-500">
                          {agent.agentId}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-gray-900">
                        {formatCurrency(agent.totalRevenue)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {agent.transactionCount} transactions
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Process Payments Tab */}
        {activeTab === 'process' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <CreditCardIcon size={20} className="text-blue-600 mr-2" />
                    Process Payments
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">Create and process new transactions as an administrator</p>
                </div>
                <div className="text-sm text-gray-500">
                  Admin Mode: <span className="font-semibold text-red-600">{user?.firstName} {user?.lastName}</span>
                </div>
              </div>
            </div>
            <div className="p-6">
              <EnhancedPaymentPortal 
                agentId="ADMIN"
                onTransactionComplete={(transaction) => {
                  // Refresh dashboard data when transaction is completed
                  loadDashboardData();
                  loadTransactions();
                }}
              />
            </div>
          </div>
        )}

        {/* All Transactions Tab */}
        {activeTab === 'transactions' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  All Transactions ({transactions.length})
                </h3>
                <div className="flex items-center space-x-2">
                  <select
                    value={sortField}
                    onChange={(e) => setSortField(e.target.value)}
                    className="form-select text-sm rounded border-gray-300"
                  >
                    <option value="createdAt">Created Date</option>
                    <option value="amounts.total">Amount</option>
                    <option value="client.name">Client Name</option>
                    <option value="agentId">Agent</option>
                    <option value="status">Status</option>
                  </select>
                  <button
                    onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                  >
                    {sortOrder === 'desc' ? '↓' : '↑'}
                  </button>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Transaction ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Agent
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payment Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {transactions.map((transaction) => (
                    <tr key={transaction._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="text-sm font-medium text-gray-900">
                            {transaction.transactionId}
                          </span>
                          {transaction.adminFlags?.requiresReview && (
                            <AlertTriangle size={16} className="ml-2 text-orange-500" />
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{transaction.client.name}</div>
                          <div className="text-sm text-gray-500">{transaction.client.email}</div>
                          {transaction.ghlContactId && (
                            <div className="text-xs text-blue-600">GHL: {transaction.ghlContactId}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{transaction.agentId}</div>
                          <div className="text-sm text-gray-500">{transaction.agentName}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-semibold text-gray-900">
                            {formatCurrency(transaction.amounts.total)}
                          </div>
                          {transaction.ccToCompAmount > 0 && (
                            <div className="text-xs text-purple-600">
                              CC to COMP: {formatCurrency(transaction.ccToCompAmount)}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {transaction.paymentType}
                        {transaction.policyNumber && (
                          <div className="text-xs text-gray-500">#{transaction.policyNumber}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(transaction.status)}`}>
                          {transaction.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(transaction.createdAt).toLocaleDateString()}
                        <div className="text-xs text-gray-500">
                          {new Date(transaction.createdAt).toLocaleTimeString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button 
                            onClick={() => {
                              setSelectedTransaction(transaction);
                              setShowTransactionModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <Eye size={16} />
                          </button>
                          <button 
                            onClick={() => {
                              setSelectedTransaction(transaction);
                              setShowNotesModal(true);
                            }}
                            className="text-gray-600 hover:text-gray-900"
                          >
                            <Edit size={16} />
                          </button>
                          {transaction.status === 'completed' && (
                            <button 
                              onClick={() => generateReceipt(transaction.transactionId, transaction._id)}
                              className="text-green-600 hover:text-green-900"
                            >
                              <FileText size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Agent Performance Tab */}
        {activeTab === 'agents' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="p-6 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900">Agent Performance Metrics</h3>
              </div>
              <div className="p-6">
                <div className="space-y-6">
                  {agents.map((agent) => (
                    <div key={agent.agentId} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-medium text-gray-900">
                            {agent.firstName} {agent.lastName}
                          </h4>
                          <p className="text-sm text-gray-500">Agent ID: {agent.agentId}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-gray-900">
                            {formatCurrency(agent.metrics?.totalRevenue || 0)}
                          </p>
                          <p className="text-sm text-gray-500">
                            {agent.metrics?.transactionCount || 0} transactions
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Success Rate</p>
                          <p className="font-semibold text-green-600">
                            {agent.metrics?.successRate || 0}%
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">Avg Transaction</p>
                          <p className="font-semibold text-gray-900">
                            {formatCurrency(agent.metrics?.avgTransaction || 0)}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">HCCs Pending</p>
                          <p className="font-semibold text-orange-600">
                            {agent.metrics?.pendingHCCs || 0}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Transaction Detail Modal */}
        {showTransactionModal && selectedTransaction && (
          <TransactionDetailModal
            transaction={selectedTransaction}
            onClose={() => {
              setShowTransactionModal(false);
              setSelectedTransaction(null);
            }}
            onGenerateReceipt={generateReceipt}
          />
        )}

        {/* Notes Modal */}
        {showNotesModal && selectedTransaction && (
          <NotesModal
            transaction={selectedTransaction}
            onClose={() => {
              setShowNotesModal(false);
              setSelectedTransaction(null);
            }}
            onUpdate={loadTransactions}
          />
        )}
      </div>
    </div>
  );
};

// Transaction Detail Modal Component
const TransactionDetailModal = ({ transaction, onClose, onGenerateReceipt }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold text-gray-900">
              Transaction Details: {transaction.transactionId}
            </h3>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <XCircle size={24} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Client Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
              <User size={18} className="mr-2 text-blue-600" />
              Client Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Name</p>
                <p className="font-medium text-gray-900">{transaction.client.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="font-medium text-gray-900">{transaction.client.email}</p>
              </div>
              {transaction.client.phone && (
                <div>
                  <p className="text-sm text-gray-600">Phone</p>
                  <p className="font-medium text-gray-900">{transaction.client.phone}</p>
                </div>
              )}
              {transaction.ghlContactId && (
                <div>
                  <p className="text-sm text-gray-600">GHL Contact ID</p>
                  <p className="font-medium text-blue-900">{transaction.ghlContactId}</p>
                </div>
              )}
            </div>
          </div>

          {/* Payment Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
              <DollarSign size={18} className="mr-2 text-green-600" />
              Payment Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <p className="text-sm text-gray-600">Premium</p>
                <p className="text-lg font-bold text-gray-900">
                  {formatCurrency(transaction.amounts.premium)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Broker Fee</p>
                <p className="text-lg font-bold text-gray-900">
                  {formatCurrency(transaction.amounts.brokerFee)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Total</p>
                <p className="text-xl font-bold text-blue-600">
                  {formatCurrency(transaction.amounts.total)}
                </p>
              </div>
            </div>

            {transaction.ccToCompAmount > 0 && (
              <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                <p className="text-sm text-purple-700">
                  <strong>CC to COMP Amount:</strong> {formatCurrency(transaction.ccToCompAmount)}
                  <br />
                  <span className="text-xs">Client pays this amount directly to the carrier</span>
                </p>
              </div>
            )}
          </div>

          {/* Charge Now Payments */}
          {transaction.chargeNow && transaction.chargeNow.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-3">Immediate Charges</h4>
              <div className="space-y-2">
                {transaction.chargeNow.map((payment, index) => (
                  <div key={index} className="flex items-center justify-between bg-white rounded p-3">
                    <div className="flex items-center space-x-3">
                      {payment.paymentMethod === 'cash' ? (
                        <Banknote size={18} className="text-green-600" />
                      ) : payment.paymentMethod === 'cc_to_comp' ? (
                        <div className="flex items-center">
                          <CreditCard size={18} className="text-purple-600" />
                          <span className="text-xs text-purple-600 ml-1">COMP</span>
                        </div>
                      ) : (
                        <CreditCard size={18} className="text-gray-400" />
                      )}
                      <div>
                        <p className="font-medium text-gray-900">
                          {formatCurrency(payment.amount)}
                        </p>
                        <p className="text-sm text-gray-500 capitalize">
                          {payment.paymentMethod === 'cc_to_comp' ? 'CC to COMP' : payment.paymentMethod}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        payment.status === 'completed' ? 'text-green-700 bg-green-100' :
                        payment.status === 'declined' ? 'text-red-700 bg-red-100' :
                        'text-yellow-700 bg-yellow-100'
                      }`}>
                        {payment.status}
                      </span>
                      {payment.status === 'declined' && payment.declineReason && (
                        <p className="text-xs text-red-600 mt-1">{payment.declineReason}</p>
                      )}
                      {payment.status === 'completed' && (
                        <button
                          onClick={() => onGenerateReceipt(transaction.transactionId, payment._id)}
                          className="ml-2 text-blue-600 hover:text-blue-900 text-xs"
                        >
                          Receipt
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Scheduled Payments */}
          {transaction.scheduledPayments && transaction.scheduledPayments.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-3">Scheduled Payments (HCCs)</h4>
              <div className="space-y-2">
                {transaction.scheduledPayments.map((payment, index) => (
                  <div key={index} className="flex items-center justify-between bg-white rounded p-3">
                    <div className="flex items-center space-x-3">
                      <Calendar size={18} className="text-blue-600" />
                      <div>
                        <p className="font-medium text-gray-900">
                          {formatCurrency(payment.amount)}
                        </p>
                        <p className="text-sm text-gray-500">
                          Due: {new Date(payment.date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        payment.status === 'completed' ? 'text-green-700 bg-green-100' :
                        payment.status === 'declined' ? 'text-red-700 bg-red-100' :
                        'text-yellow-700 bg-yellow-100'
                      }`}>
                        {payment.status}
                      </span>
                      {payment.status === 'completed' && (
                        <button
                          onClick={() => onGenerateReceipt(transaction.transactionId, payment._id)}
                          className="ml-2 text-blue-600 hover:text-blue-900 text-xs"
                        >
                          Receipt
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes Section */}
          {transaction.notes && transaction.notes.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-3">Notes</h4>
              <div className="space-y-2">
                {transaction.notes.map((note, index) => (
                  <div key={index} className="bg-white rounded p-3 border-l-4 border-blue-500">
                    <p className="text-sm text-gray-900">{note.text}</p>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-xs text-gray-500">
                        {note.noteType} • {new Date(note.createdAt).toLocaleString()}
                      </p>
                      {note.isAgentPrivate && (
                        <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded">
                          Agent Private
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}


        {activeTab === 'links' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-red-600">Active Payment Links Management</h2>
                <button 
                  onClick={loadPaymentLinks}
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
                >
                  Refresh Links ({paymentLinks.length})
                </button>
              </div>
              
              {linksLoading ? (
                <div className="text-center py-4">Loading payment links...</div>
              ) : (
                <div className="space-y-3">
                  {paymentLinks.length === 0 ? (
                    <p className="text-gray-500">No active payment links found.</p>
                  ) : (
                    paymentLinks.map((link) => (
                      <div key={link.linkId} className="bg-gray-50 p-4 rounded-lg border">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h3 className="font-medium text-gray-900">
                                {link.clientInfo.firstName} {link.clientInfo.lastName}
                              </h3>
                              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                link.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {link.isActive ? 'Active' : 'Expired'}
                              </span>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                              <div>
                                <span className="font-medium">Amount:</span> ${link.amount?.toFixed(2) || '0.00'}
                              </div>
                              <div>
                                <span className="font-medium">Agent:</span> {link.agentId}
                              </div>
                              <div>
                                <span className="font-medium">Created:</span> {new Date(link.createdAt).toLocaleDateString()}
                              </div>
                              <div>
                                <span className="font-medium">Expires:</span> {new Date(link.expiresAt).toLocaleDateString()}
                              </div>
                            </div>
                            <div className="mt-2 text-xs text-gray-500">
                              <span className="font-medium">Link ID:</span> {link.linkId}
                            </div>
                            <div className="mt-2">
                              <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
                                Link URL: {`${window.location.origin}/declined-payment/${link.linkId}`}
                              </span>
                            </div>
                          </div>
                          <div className="flex space-x-2 ml-4">
                            {link.isActive && (
                              <>
                                <button 
                                  onClick={() => extendLink(link.linkId)}
                                  className="bg-green-500 text-white px-3 py-1 text-sm rounded hover:bg-green-600 transition-colors"
                                >
                                  Extend
                                </button>
                                <button 
                                  onClick={() => deactivateLink(link.linkId)}
                                  className="bg-red-500 text-white px-3 py-1 text-sm rounded hover:bg-red-600 transition-colors"
                                >
                                  Deactivate
                                </button>
                              </>
                            )}
                            <button 
                              onClick={() => navigator.clipboard.writeText(`${window.location.origin}/declined-payment/${link.linkId}`)}
                              className="bg-gray-500 text-white px-3 py-1 text-sm rounded hover:bg-gray-600 transition-colors"
                            >
                              Copy URL
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'reports' && (
          <AdminReports 
            dashboardData={dashboardData}
            agents={agents}
            transactions={transactions}
            filters={filters}
            setFilters={setFilters}
            exportData={exportData}
          />
        )}
        </div>
      </div>
    </div>
  );
};


// Notes Modal Component
const NotesModal = ({ transaction, onClose, onUpdate }) => {
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState('general');
  const [isPrivate, setIsPrivate] = useState(false);
  const [priority, setPriority] = useState(transaction.adminFlags?.priority || 'medium');

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    try {
      await axiosInstance.post(`/admin/transactions/${transaction._id}/notes`, {
        text: newNote,
        noteType,
        isAgentPrivate: isPrivate
      });
      
      setNewNote('');
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error adding note:', error);
    }
  };

  const handleUpdatePriority = async () => {
    try {
      await axiosInstance.patch(`/admin/transactions/${transaction._id}/priority`, {
        priority
      });
      
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error updating priority:', error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold text-gray-900">
              Manage Transaction: {transaction.transactionId}
            </h3>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <XCircle size={24} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Priority Management */}
          <div>
            <h4 className="font-semibold text-gray-900 mb-3">Priority Level</h4>
            <div className="flex items-center space-x-4">
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="form-select rounded border-gray-300"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
              <button
                onClick={handleUpdatePriority}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                Update Priority
              </button>
            </div>
          </div>

          {/* Add New Note */}
          <div>
            <h4 className="font-semibold text-gray-900 mb-3">Add Note</h4>
            <div className="space-y-4">
              <textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg h-24 resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your note here..."
              />
              <div className="flex items-center space-x-4">
                <select
                  value={noteType}
                  onChange={(e) => setNoteType(e.target.value)}
                  className="form-select rounded border-gray-300"
                >
                  <option value="general">General</option>
                  <option value="collection">Collection</option>
                  <option value="payment_issue">Payment Issue</option>
                  <option value="client_request">Client Request</option>
                  <option value="admin_note">Admin Note</option>
                </select>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={isPrivate}
                    onChange={(e) => setIsPrivate(e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Agent Private Note</span>
                </label>
                <button
                  onClick={handleAddNote}
                  className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                >
                  Add Note
                </button>
              </div>
            </div>
          </div>

          {/* Existing Notes */}
          {transaction.notes && transaction.notes.length > 0 && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Existing Notes</h4>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {transaction.notes.map((note, index) => (
                  <div key={index} className="bg-gray-50 rounded p-3 border-l-4 border-blue-500">
                    <p className="text-sm text-gray-900">{note.text}</p>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-xs text-gray-500">
                        {note.noteType} • {new Date(note.createdAt).toLocaleString()}
                      </p>
                      {note.isAgentPrivate && (
                        <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded">
                          Agent Private
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComprehensiveAdminDashboard;